import React from 'react';
import { useTranslation } from 'react-i18next';

import ComboBox from '../../Fundamentals/ComboBox';
import { Checkbox } from '../../FormComponents/Checkbox';
import FilterCard from './FilterCardBeta';

const PricingFilterBar = ({
    // Array-based filter state (plural names)
    productFilters,
    onProductFiltersChange,
    selectedPackages,
    onPackagesChange,
    selectedProductTypes,
    onProductTypesChange,
    selectedTiers,
    onTiersChange,
    selectedPlans,
    onPlansChange,
    modificationStatuses,
    onModificationStatusesChange,
    // Dropdown options
    productOptions,
    packageOptions,
    productTypeOptions,
    tierOptions,
    planOptions,
    modificationStatusOptions,
}) => {
    const { t } = useTranslation();

    const handlePlanToggle = (planId) => {
        const exists = selectedPlans.some(p => p.id === planId);
        if (exists) {
            onPlansChange(selectedPlans.filter(p => p.id !== planId));
        } else {
            const plan = planOptions.find(p => p.id === planId);
            if (plan) onPlansChange([...selectedPlans, plan]);
        }
    };

    // Disable all filters until at least one package is selected
    const hasSelectedPackages = selectedPackages.length > 0;

    return (
        <div className="pricing-filter-bar">
            <div className="filter-grid">
                {/* Package Filter */}
                <FilterCard 
                    selectedItems={selectedPackages}
                    onRemoveItem={(item) => onPackagesChange(selectedPackages.filter(p => p.id !== item.id))}
                >
                    <ComboBox
                        placeholder={t('priceSimulation.package')}
                        options={packageOptions}
                        value={selectedPackages.map(p => p.id)}
                        onChange={(values, options) => onPackagesChange(options)}
                        valueKey="id"
                        labelKey="name"
                        isMulti={true}
                        hideInternalChips={true}
                    />
                </FilterCard>

                {/* Product Filter */}
                <FilterCard 
                    selectedItems={productFilters}
                    onRemoveItem={(item) => onProductFiltersChange(productFilters.filter(p => p.id !== item.id))}
                    disabled
                    isNotImplemented
                >
                    <ComboBox
                        placeholder={t('priceSimulation.product')} 
                        options={productOptions} 
                        value={productFilters.map(p => p.id)}
                        onChange={(values, options) => onProductFiltersChange(options)}
                        valueKey="id"
                        labelKey="name"
                        isMulti={true}
                        hideInternalChips={true}
                        disabled={!hasSelectedPackages}
                    />
                </FilterCard>

                {/* Product Type Filter */}
                <FilterCard
                    selectedItems={selectedProductTypes}
                    onRemoveItem={(item) => onProductTypesChange(selectedProductTypes.filter(p => p.id !== item.id))}
                >
                    <ComboBox
                        placeholder={t('priceSimulation.productType')}
                        options={productTypeOptions}
                        value={selectedProductTypes.map(p => p.id)}
                        onChange={(values, options) => onProductTypesChange(options)}
                        valueKey="id"
                        labelKey="name"
                        isMulti={true}
                        hideInternalChips={true}
                        disabled={!hasSelectedPackages}
                    />
                </FilterCard>

                {/* Tier Filter */}
                <FilterCard 
                    selectedItems={selectedTiers}
                    onRemoveItem={(item) => onTiersChange(selectedTiers.filter(t => t.id !== item.id))}
                >
                    <ComboBox 
                        placeholder={t('priceSimulation.tier')} 
                        options={tierOptions} 
                        value={selectedTiers.map(t => t.id)}
                        onChange={(values, options) => onTiersChange(options)}
                        valueKey="id" 
                        labelKey="name"
                        isMulti={true}
                        hideInternalChips={true}
                        disabled={!hasSelectedPackages}
                    />
                </FilterCard>

                {/* Plan Filter */}
                <FilterCard 
                    disabled={!hasSelectedPackages}
                    selectedItems={selectedPlans}
                    onRemoveItem={(item) => onPlansChange(selectedPlans.filter(p => p.id !== item.id))}
                    renderCustomBody={() => (
                        <div className="filter-card-plan-checkboxes">
                            {planOptions.map((plan) => (
                                <Checkbox
                                    key={plan.id}
                                    label={plan.name}
                                    checked={selectedPlans.some(p => p.id === plan.id)}
                                    onChange={() => handlePlanToggle(plan.id)}
                                    id={`plan-${plan.id}`}
                                    disabled={!hasSelectedPackages}
                                />
                            ))}
                        </div>
                    )}
                >
                    <div className="filter-card-plan-label">
                        {t('priceSimulation.plan')}
                    </div>
                </FilterCard>

                {/* Status Filter */}
                <FilterCard 
                    selectedItems={modificationStatuses}
                    onRemoveItem={(item) => onModificationStatusesChange(modificationStatuses.filter(s => s.id !== item.id))}
                >
                    <ComboBox
                        placeholder={t('priceSimulation.modificationStatus')}
                        options={modificationStatusOptions}
                        value={modificationStatuses.map(s => s.id)}
                        onChange={(values, options) => onModificationStatusesChange(options)}
                        valueKey="id"
                        labelKey="name"
                        isMulti={true}
                        hideInternalChips={true}
                        disabled={!hasSelectedPackages}
                    />
                </FilterCard>
            </div>
        </div>
    );
};

export default PricingFilterBar;
